/**
 * House & Land Packages — Filters, Sorting & Mobile Drawer
 * Vanilla JS — no Alpine, no Vue. lodash.debounce available if needed.
 */

'use strict';

// ─── Dual-handle Range Slider ────────────────────────────────────────────────

class DualRangeSlider {
    constructor(container, onUpdate) {
        this.container = container;
        this.onUpdate  = onUpdate || null;
        this.low       = container.querySelector('.hl-range-low');
        this.high      = container.querySelector('.hl-range-high');
        this.fill      = container.querySelector('.hl-range-fill');
        this.absMin    = parseFloat(this.low.min);
        this.absMax    = parseFloat(this.high.max);

        this._onLowInput  = this._onLowInput.bind(this);
        this._onHighInput = this._onHighInput.bind(this);

        this.low.addEventListener('input',  this._onLowInput);
        this.high.addEventListener('input', this._onHighInput);

        this._updateFill();
    }

    _onLowInput() {
        if (parseFloat(this.low.value) > parseFloat(this.high.value)) {
            this.low.value = this.high.value;
        }
        this._updateFill();
        this.onUpdate && this.onUpdate(this.getValues());
    }

    _onHighInput() {
        if (parseFloat(this.high.value) < parseFloat(this.low.value)) {
            this.high.value = this.low.value;
        }
        this._updateFill();
        this.onUpdate && this.onUpdate(this.getValues());
    }

    _updateFill() {
        const range = this.absMax - this.absMin;
        if (range === 0) return;
        const lowPct  = ((parseFloat(this.low.value)  - this.absMin) / range) * 100;
        const highPct = ((parseFloat(this.high.value) - this.absMin) / range) * 100;
        this.fill.style.left  = lowPct + '%';
        this.fill.style.width = (highPct - lowPct) + '%';
    }

    getValues() {
        return { low: parseFloat(this.low.value), high: parseFloat(this.high.value) };
    }

    setValues(low, high) {
        this.low.value  = Math.max(this.absMin, Math.min(this.absMax, low));
        this.high.value = Math.max(this.absMin, Math.min(this.absMax, high));
        this._updateFill();
    }

    reset() { this.setValues(this.absMin, this.absMax); }

    isDefault() {
        return parseFloat(this.low.value)  === this.absMin &&
               parseFloat(this.high.value) === this.absMax;
    }

    destroy() {
        this.low.removeEventListener('input',  this._onLowInput);
        this.high.removeEventListener('input', this._onHighInput);
    }
}

// ─── Panel Initialisation ────────────────────────────────────────────────────

function initHLFilters(panel) {
    const fc = panel.querySelector('.hl-filters-container');
    if (!fc) return;

    // Absolute bounds from data attributes on the filter container
    const absMin = {
        price: parseFloat(fc.dataset.priceMin) || 0,
        area:  parseFloat(fc.dataset.areaMin)  || 0,
    };
    const absMax = {
        price: parseFloat(fc.dataset.priceMax) || 2000000,
        area:  parseFloat(fc.dataset.areaMax)  || 500,
    };

    // ── DOM refs ─────────────────────────────────────────────────────────────
    const cards      = [...panel.querySelectorAll('.filterable-hl-card')];
    const cardsGrid  = panel.querySelector('.hl-cards-grid');
    const noResults  = panel.querySelector('.hl-no-results');
    const countEl    = panel.querySelector('.hl-count');
    const countLabel = panel.querySelector('.hl-count-label');
    const badge      = panel.querySelector('.hl-active-badge');
    const clearBtns  = panel.querySelectorAll('.hl-clear-btn');

    const desktopBar   = fc.querySelector('.hl-desktop-bar');
    const mobileBar    = fc.querySelector('.hl-mobile-bar');
    const sortDesktop  = desktopBar  ? desktopBar.querySelector('[data-hl-filter="sort"]')  : null;
    const sortMobile   = mobileBar   ? mobileBar.querySelector('[data-hl-filter="sort"]')   : null;
    const desktopBeds  = desktopBar  ? desktopBar.querySelector('[data-hl-filter="beds"]')  : null;
    const desktopBaths = desktopBar  ? desktopBar.querySelector('[data-hl-filter="baths"]') : null;
    const deskPriceEl  = desktopBar  ? desktopBar.querySelector('[data-hl-filter="price"]') : null;
    const deskAreaEl   = desktopBar  ? desktopBar.querySelector('[data-hl-filter="area"]')  : null;

    const drawer       = panel.querySelector('.hl-drawer');
    const drawerClose  = drawer ? drawer.querySelector('.hl-drawer-close')   : null;
    const drawerApply  = drawer ? drawer.querySelector('.hl-drawer-apply')   : null;
    const drawerClear  = drawer ? drawer.querySelector('.hl-drawer-clear')   : null;
    const drawerBackdrop = drawer ? drawer.querySelector('.hl-drawer-backdrop') : null;
    const drawerBeds   = drawer ? drawer.querySelector('[data-hl-drawer-filter="beds"]')  : null;
    const drawerBaths  = drawer ? drawer.querySelector('[data-hl-drawer-filter="baths"]') : null;
    const drawPriceEl  = drawer ? drawer.querySelector('[data-hl-drawer-filter="price"]') : null;
    const drawAreaEl   = drawer ? drawer.querySelector('[data-hl-drawer-filter="area"]')  : null;
    const drawerOpenBtn = fc.querySelector('.hl-mobile-filters-btn');

    // Range-display spans (updated by JS as sliders move)
    const displayEls = {
        'price':         fc.querySelector('[data-hl-range-display="price"]'),
        'area':          fc.querySelector('[data-hl-range-display="area"]'),
        'price-drawer':  drawer ? drawer.querySelector('[data-hl-range-display="price-drawer"]') : null,
        'area-drawer':   drawer ? drawer.querySelector('[data-hl-range-display="area-drawer"]')  : null,
    };

    // ── Filter state (source of truth) ───────────────────────────────────────
    const state = {
        beds:     '',
        baths:    '',
        priceMin: absMin.price,
        priceMax: absMax.price,
        areaMin:  absMin.area,
        areaMax:  absMax.area,
        sort:     'recent',
    };

    // Staging state for the mobile drawer — committed on Apply
    const staging = { ...state };

    // Slider instances keyed for later access
    const sliders = {};

    // ── Display helper ────────────────────────────────────────────────────────

    function updateDisplay(key, low, high) {
        const el = displayEls[key];
        if (!el) return;
        const isPrice = key.startsWith('price');
        const fmt     = v => isPrice ? '$' + Math.round(v).toLocaleString() : Math.round(v) + 'm²';
        const lowEl   = el.querySelector('.hl-range-display-low');
        const highEl  = el.querySelector('.hl-range-display-high');
        if (lowEl)  lowEl.textContent  = fmt(low);
        if (highEl) highEl.textContent = fmt(high);
    }

    // ── Slider initialisation ─────────────────────────────────────────────────

    if (deskPriceEl) {
        sliders.deskPrice = new DualRangeSlider(deskPriceEl, ({ low, high }) => {
            state.priceMin = low;
            state.priceMax = high;
            updateDisplay('price', low, high);
            applyFilters();
        });
    }

    if (deskAreaEl) {
        sliders.deskArea = new DualRangeSlider(deskAreaEl, ({ low, high }) => {
            state.areaMin = low;
            state.areaMax = high;
            updateDisplay('area', low, high);
            applyFilters();
        });
    }

    if (drawPriceEl) {
        sliders.drawPrice = new DualRangeSlider(drawPriceEl, ({ low, high }) => {
            staging.priceMin = low;
            staging.priceMax = high;
            updateDisplay('price-drawer', low, high);
        });
    }

    if (drawAreaEl) {
        sliders.drawArea = new DualRangeSlider(drawAreaEl, ({ low, high }) => {
            staging.areaMin = low;
            staging.areaMax = high;
            updateDisplay('area-drawer', low, high);
        });
    }

    // ── Filter + Sort logic ───────────────────────────────────────────────────

    function applyFilters() {
        let visible = 0;

        cards.forEach(card => {
            const beds  = card.dataset.beds  !== '' ? parseInt(card.dataset.beds,  10) : null;
            const baths = card.dataset.baths !== '' ? parseInt(card.dataset.baths, 10) : null;
            const price = card.dataset.price !== '' ? parseFloat(card.dataset.price)   : null;
            const area  = card.dataset.area  !== '' ? parseFloat(card.dataset.area)    : null;

            let show = true;

            // Beds/baths: exact match — no data → always show
            if (state.beds  !== '' && beds  !== null && beds  !== parseInt(state.beds,  10)) show = false;
            if (state.baths !== '' && baths !== null && baths !== parseInt(state.baths, 10)) show = false;

            // Price/area: within range — no data → always show
            if (price !== null && (price < state.priceMin || price > state.priceMax)) show = false;
            if (area  !== null && (area  < state.areaMin  || area  > state.areaMax))  show = false;

            card.dataset.filtered = show ? 'false' : 'true';
            card.classList.toggle('hidden', !show);
            if (show) visible++;
        });

        // Sort + re-append
        [...cards].sort((a, b) => {
            switch (state.sort) {
                case 'az': return (a.dataset.title || '').localeCompare(b.dataset.title || '');
                case 'za': return (b.dataset.title || '').localeCompare(a.dataset.title || '');
                case 'price-asc': {
                    const pA = a.dataset.price !== '' ? parseFloat(a.dataset.price) : null;
                    const pB = b.dataset.price !== '' ? parseFloat(b.dataset.price) : null;
                    if (pA === null && pB === null) return 0;
                    if (pA === null) return 1;
                    if (pB === null) return -1;
                    return pA - pB;
                }
                case 'price-desc': {
                    const pA = a.dataset.price !== '' ? parseFloat(a.dataset.price) : null;
                    const pB = b.dataset.price !== '' ? parseFloat(b.dataset.price) : null;
                    if (pA === null && pB === null) return 0;
                    if (pA === null) return 1;
                    if (pB === null) return -1;
                    return pB - pA;
                }
                default: // 'recent' — data-date is a Unix timestamp (desc)
                    return parseInt(b.dataset.date || 0, 10) - parseInt(a.dataset.date || 0, 10);
            }
        }).forEach(card => cardsGrid && cardsGrid.appendChild(card));

        // Results count
        if (countEl)    countEl.textContent    = visible;
        if (countLabel) countLabel.textContent = visible === 1 ? 'package' : 'packages';

        // No-results message
        if (noResults && cardsGrid) {
            noResults.classList.toggle('hidden', visible > 0);
            cardsGrid.classList.toggle('hidden',  visible === 0);
        }

        updateBadge();
    }

    // ── Badge + clear helpers ─────────────────────────────────────────────────

    function countActiveFilters() {
        let n = 0;
        if (state.beds  !== '') n++;
        if (state.baths !== '') n++;
        if (sliders.deskPrice && !sliders.deskPrice.isDefault()) n++;
        if (sliders.deskArea  && !sliders.deskArea.isDefault())  n++;
        return n;
    }

    function updateBadge() {
        if (!badge) return;
        const n = countActiveFilters();
        badge.textContent = n;
        badge.classList.toggle('hidden', n === 0);
        badge.classList.toggle('flex',   n > 0);
    }

    function clearAll() {
        state.beds = ''; state.baths = '';
        state.priceMin = absMin.price; state.priceMax = absMax.price;
        state.areaMin  = absMin.area;  state.areaMax  = absMax.area;

        if (desktopBeds)  desktopBeds.value  = '';
        if (desktopBaths) desktopBaths.value = '';
        if (sliders.deskPrice) { sliders.deskPrice.reset(); updateDisplay('price', absMin.price, absMax.price); }
        if (sliders.deskArea)  { sliders.deskArea.reset();  updateDisplay('area',  absMin.area,  absMax.area);  }

        // Mirror to drawer staging + inputs
        Object.assign(staging, state);
        if (drawerBeds)  drawerBeds.value  = '';
        if (drawerBaths) drawerBaths.value = '';
        if (sliders.drawPrice) { sliders.drawPrice.reset(); updateDisplay('price-drawer', absMin.price, absMax.price); }
        if (sliders.drawArea)  { sliders.drawArea.reset();  updateDisplay('area-drawer',  absMin.area,  absMax.area);  }

        applyFilters();
    }

    // ── Desktop event bindings ────────────────────────────────────────────────

    desktopBeds?.addEventListener('change',  () => { state.beds  = desktopBeds.value;  applyFilters(); });
    desktopBaths?.addEventListener('change', () => { state.baths = desktopBaths.value; applyFilters(); });

    function onSortChange(value) {
        state.sort = value;
        if (sortDesktop && sortDesktop.value !== value) sortDesktop.value = value;
        if (sortMobile  && sortMobile.value  !== value) sortMobile.value  = value;
        applyFilters();
    }
    sortDesktop?.addEventListener('change', () => onSortChange(sortDesktop.value));
    sortMobile?.addEventListener('change',  () => onSortChange(sortMobile.value));

    clearBtns.forEach(btn => btn.addEventListener('click', clearAll));

    // ── Mobile drawer ─────────────────────────────────────────────────────────

    function openDrawer() {
        if (!drawer) return;
        // Sync staging from current committed state
        Object.assign(staging, state);
        if (drawerBeds)  drawerBeds.value  = state.beds;
        if (drawerBaths) drawerBaths.value = state.baths;
        if (sliders.drawPrice) { sliders.drawPrice.setValues(state.priceMin, state.priceMax); updateDisplay('price-drawer', state.priceMin, state.priceMax); }
        if (sliders.drawArea)  { sliders.drawArea.setValues(state.areaMin, state.areaMax);    updateDisplay('area-drawer',  state.areaMin,  state.areaMax);  }
        drawer.dataset.open = 'true';
        drawer.removeAttribute('aria-hidden');
        document.body.style.overflow = 'hidden';
    }

    function closeDrawer() {
        if (!drawer) return;
        drawer.dataset.open = 'false';
        drawer.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    }

    drawerOpenBtn?.addEventListener('click', openDrawer);
    drawerClose?.addEventListener('click',   closeDrawer);
    drawerBackdrop?.addEventListener('click', closeDrawer);

    drawerApply?.addEventListener('click', () => {
        // Commit staging to state
        state.beds     = drawerBeds  ? drawerBeds.value  : state.beds;
        state.baths    = drawerBaths ? drawerBaths.value : state.baths;
        state.priceMin = staging.priceMin;
        state.priceMax = staging.priceMax;
        state.areaMin  = staging.areaMin;
        state.areaMax  = staging.areaMax;

        // Sync desktop inputs to match
        if (desktopBeds)  desktopBeds.value  = state.beds;
        if (desktopBaths) desktopBaths.value = state.baths;
        if (sliders.deskPrice) { sliders.deskPrice.setValues(state.priceMin, state.priceMax); updateDisplay('price', state.priceMin, state.priceMax); }
        if (sliders.deskArea)  { sliders.deskArea.setValues(state.areaMin, state.areaMax);    updateDisplay('area',  state.areaMin,  state.areaMax);  }

        applyFilters();
        closeDrawer();
    });

    drawerClear?.addEventListener('click', () => { clearAll(); closeDrawer(); });

    // Close on Escape key
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && drawer && drawer.dataset.open === 'true') closeDrawer();
    });

    // ── Initial render ────────────────────────────────────────────────────────
    applyFilters();
}

// ─── Boot ─────────────────────────────────────────────────────────────────────

window.addEventListener('load', () => {
    document.querySelectorAll('.packages-panel').forEach(panel => initHLFilters(panel));
});

